import { useState } from "react";
import axios from "axios";
import { productImagesService } from "../../api_client";
import {
  CreateProductImageUploadUrlDtoContentTypeEnum,
  type CreateProductImageUploadUrlDto,
} from "../../../api";
import { showErrorToast } from "../../../utils/toasts";

const ACCEPTED_TYPES: CreateProductImageUploadUrlDtoContentTypeEnum[] = [
  CreateProductImageUploadUrlDtoContentTypeEnum.ImageJpeg,
  CreateProductImageUploadUrlDtoContentTypeEnum.ImagePng,
  CreateProductImageUploadUrlDtoContentTypeEnum.ImageWebp,
];

const MAX_BYTES = 10 * 1024 * 1024;

function asAcceptedType(
  type: string,
): CreateProductImageUploadUrlDtoContentTypeEnum | null {
  return (ACCEPTED_TYPES as string[]).includes(type)
    ? (type as CreateProductImageUploadUrlDtoContentTypeEnum)
    : null;
}

export interface UseProductImageUploadReturn {
  isUploading: boolean;
  progress: number;
  upload: (file: File) => Promise<string | null>;
}

export function useProductImageUpload(): UseProductImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (file: File): Promise<string | null> => {
    const contentType = asAcceptedType(file.type);
    if (!contentType) {
      showErrorToast("Unsupported image type. Use JPEG, PNG, or WebP.");
      return null;
    }
    if (file.size > MAX_BYTES) {
      showErrorToast("Image exceeds the 10MB limit.");
      return null;
    }

    setIsUploading(true);
    setProgress(0);

    try {
      const body: CreateProductImageUploadUrlDto = {
        contentType,
        fileSize: file.size,
      };
      const res = await productImagesService.createUploadUrl(body);
      const { uploadUrl, objectKey, headers } = res.data;

      const putHeaders: Record<string, string> = {
        ...(headers as Record<string, string>),
      };
      if (!putHeaders["Content-Type"]) {
        putHeaders["Content-Type"] = file.type;
      }

      await axios.put(uploadUrl, file, {
        headers: putHeaders,
        onUploadProgress: (event) => {
          if (!event.total) return;
          setProgress(event.loaded / event.total);
        },
      });

      setProgress(1);
      return objectKey;
    } catch (error) {
      console.error("Product image upload error:", error);
      showErrorToast("Failed to upload image. Please try again.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, progress, upload };
}
